/**
 * Sensor Fusion Acceleration Calculator
 *
 * Combines accelerometer, gyroscope, and GPS data for accurate acceleration
 * by correcting for device orientation (lean/pitch/yaw) and removing gravity.
 *
 * Problem: Raw accelerometer readings are in the bike's reference frame.
 * When leaned at 45°, "lateral" axis points partially toward gravity.
 * When pitching under braking, "longitudinal" axis picks up gravity.
 *
 * Solution:
 * 1. Track full 3D orientation using gyroscope (roll, pitch, yaw)
 * 2. Use accelerometer for absolute reference (prevents drift). Pitch is
 *    estimated from the gravity component only — the GPS speed trend
 *    removes the dynamic part — with the sign matching the earth-frame
 *    transform.
 * 3. Rotate accelerometer readings to earth/road frame
 * 4. Subtract gravity to get actual dynamic acceleration
 * 5. Use GPS as validation and fallback
 *
 * Gyroscope axes (standard orientation):
 *   X = Roll rate (lean left/right)
 *   Y = Pitch rate (nose up/down)
 *   Z = Yaw rate (turning left/right)
 */

import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import { AccelerationPoint } from '../domain/telemetry/processed/Acceleration'
import { AccelerometerReading } from '../domain/telemetry/raw/AccelerometerReading'
import { GyroscopeReading } from '../domain/telemetry/raw/GyroscopeReading'
import { LeanAnglePoint } from '../domain/telemetry/processed/LeanAngle'
import { FrequencyAdaptiveConfig } from '../utils/FrequencyAdaptiveConfig'
import { smoothValuesGaussian } from '../utils/smoothingUtils'
import { gpsSpeedTrendMs2 } from './accelConsistencyValidator'
import {
  estimateSensorFrameCalibration,
  applySensorFrameCalibration,
} from './sensorFrameCalibration'
import {
  averageReadingsInWindow,
  firstWindowStartExclusiveMs,
} from './imuWindowAverage'
import {
  ACCEL_CONSISTENCY_MIN_TREND_MS2,
  ACCEL_CONSISTENCY_MIN_SIGNAL_MS2,
  ACCEL_CONSISTENCY_RELATIVE_TREND_MS2,
  ACCEL_CONSISTENCY_RELATIVE_SIGNAL_FRACTION,
} from '../analysis/config/accelConsistency'
import {
  classifyVehicleLeanRegime,
  computeGpsCoordinatedLeanSeries,
} from './gpsCoordinatedLean'

interface AccelerationFusionConfig {
  /**
   * Weight for GPS validation (0-1)
   * When sensor and GPS values diverge, blend GPS estimate
   */
  gpsValidationWeight: number

  /**
   * Threshold (m/s²) for GPS correction
   * Only apply GPS correction if difference exceeds this
   */
  gpsCorrectThreshold: number

  /**
   * Minimum speed (km/h) for GPS lateral calculation
   */
  minSpeedForGpsLateral: number

  /**
   * Apply smoothing to output
   */
  smoothOutput: boolean

  /** Weight kept for the fused value when its sign contradicts a clear GPS speed trend (rest goes to the trend). */
  signMismatchFusedWeight: number
}

const DEFAULT_CONFIG: AccelerationFusionConfig = {
  gpsValidationWeight: 0.2,
  gpsCorrectThreshold: 2.0, // m/s²
  minSpeedForGpsLateral: 15,
  smoothOutput: true,
  signMismatchFusedWeight: 0.2,
}

interface Orientation3D {
  rollDeg: number // lean angle (left/right)
  pitchDeg: number // nose up/down
  yawDeg: number // heading (not critical for gravity removal)
}

// The coordinated-lean series and the leaning-vehicle gate live in
// gpsCoordinatedLean.ts (shared with the lean calculator). Re-exported here
// for compatibility with existing consumers/tests of this module's API.
export { computeGpsCoordinatedLeanSeries }

/**
 * Decoupling-lean input for track3DOrientation, gated by vehicle attitude
 * regime (classifyVehicleLeanRegime):
 * - leaning (coordinated) vehicle → the GPS coordinated lean series. The
 *   fused pipeline lean under-reads systematically in sustained corners,
 *   which under-cancels the ψ̇·sin(roll) yaw→pitch coupling and leaves a
 *   phantom pitch ∝ |lean| that leaks gravity into the longitudinal channel
 *   (~−2·sin(lean) m/s² measured on session-143033);
 * - upright vehicle → all-zero (an upright body has no yaw→pitch coupling
 *   to cancel, and the coordinated formula would report a large fake lean);
 * - unknown → null: decouple with the fused pipeline lean.
 *
 * Exported for direct unit testing of the gate (some gate outcomes are
 * indistinguishable through the calculator's public API on synthetic
 * fixtures).
 */
export function selectDecouplingLeanSeries(
  gpsPoints: GpsPoint[],
  accelerometer: AccelerometerReading[],
  coordinatedLean: number[]
): number[] | null {
  const regime = classifyVehicleLeanRegime(
    gpsPoints,
    accelerometer,
    coordinatedLean
  )
  if (regime === 'upright') return coordinatedLean.map(() => 0)
  if (regime === 'leaning') return coordinatedLean
  return null
}

/**
 * Estimate pitch from the accelerometer's gravity component.
 *
 * Sign convention matches transformToEarthFrame3D: a stationary bike
 * physically pitched by φ reads x = 9.81·sinφ, z = −9.81·cosφ, and this
 * function returns +φ so the transform recovers earthX = 0. (The previous
 * atan2(−x, …) returned −φ, which made the transform DOUBLE the gravity
 * leak instead of cancelling it.)
 *
 * dynamicLongitudinalMs2 (the GPS speed trend) is subtracted from x first:
 * under sustained acceleration the x-axis carries real dynamic acceleration
 * on top of the gravity component, and without the subtraction the
 * estimator reads it as phantom pitch (≈ 11.5° at 2 m/s²).
 */
function getPitchFromAccelerometer(
  accelX: number,
  accelY: number,
  accelZ: number,
  dynamicLongitudinalMs2: number
): number {
  const gravityX = accelX - dynamicLongitudinalMs2
  const horizontalMag = Math.sqrt(accelY * accelY + accelZ * accelZ)
  if (horizontalMag < 0.1) return 0 // Avoid division issues

  const pitchRad = Math.atan2(gravityX, horizontalMag)
  return (pitchRad * 180) / Math.PI
}

/**
 * Track 3D orientation using complementary filter.
 * Combines gyroscope (fast, but drifts) with accelerometer (absolute reference)
 */
function track3DOrientation(
  accelerometer: AccelerometerReading[],
  gyroscope: GyroscopeReading[],
  leanAngle: LeanAnglePoint[],
  gpsPoints: GpsPoint[],
  decouplingLean: number[] | null = null
): Orientation3D[] {
  const GYRO_WEIGHT = 0.97 // High-pass for gyro
  const ACCEL_WEIGHT = 1 - GYRO_WEIGHT // Low-pass for accel

  const orientations: Orientation3D[] = []

  // Initialize
  let currentRoll = 0
  let currentPitch = 0
  let currentYaw = 0

  let gyroIndex = 0
  let accelIndex = 0
  const firstWindowStartMs = firstWindowStartExclusiveMs(gpsPoints)

  for (let i = 0; i < gpsPoints.length; i++) {
    const point = gpsPoints[i]
    const timestamp = point.timestamp

    // Use pre-calculated lean angle for roll (it's already sensor-fused)
    const preCalcRoll = leanAngle[i]?.leanAngleDegrees ?? currentRoll

    // Roll (radians) for this point, used to decouple the gyro pitch rate.
    // Prefer the independent GPS coordinated lean (see
    // computeGpsCoordinatedLeanSeries): the fused lean under-reads in
    // sustained corners and under-cancels the yaw→pitch coupling. Fall back
    // to the fused lean where the GPS lean is undefined (low speed, stream
    // edges, degenerate heading) or for unclassified sessions.
    const decouplingRollDeg =
      decouplingLean !== null && Number.isFinite(decouplingLean[i])
        ? decouplingLean[i]
        : preCalcRoll
    const rollRadForPitch = (decouplingRollDeg * Math.PI) / 180

    // Find gyroscope readings between last point and this point
    const prevTimestamp = i > 0 ? gpsPoints[i - 1].timestamp : timestamp

    // Average accelerometer samples in this GPS tick's window for the
    // pitch reference (see averageReadingsInWindow) rather than holding
    // the single sample at or before the tick, which aliases ride
    // vibration into the pitch estimate.
    const accelWindow = averageReadingsInWindow(
      accelerometer,
      i > 0 ? prevTimestamp.getTime() : firstWindowStartMs,
      timestamp.getTime(),
      accelIndex
    )
    accelIndex = accelWindow.index
    const accelReading = accelWindow.reading

    // Integrate gyroscope for pitch
    while (
      gyroIndex < gyroscope.length &&
      gyroscope[gyroIndex].timestamp <= timestamp
    ) {
      const gyro = gyroscope[gyroIndex]

      // Convert to deg/s if needed
      const factor = gyro.unit === 'rad/s' ? 180 / Math.PI : 1
      const pitchRate = gyro.y * factor // Y-axis is body pitch rate (q)
      const yawRate = gyro.z * factor // Z-axis is body yaw rate (r)

      // Calculate dt
      const prevGyroTime =
        gyroIndex > 0 ? gyroscope[gyroIndex - 1].timestamp : gyro.timestamp
      const dt = (gyro.timestamp.getTime() - prevGyroTime.getTime()) / 1000

      if (dt > 0 && dt < 0.5) {
        // Attitude-complete pitch rate: in a banked turn the body pitch-rate
        // gyro carries the kinematic yaw→pitch coupling ψ̇·sin(roll). The Euler
        // pitch-rate equation θ̇ = q·cos(roll) + r·sin(roll) removes it (the
        // sign follows this codebase's leanAngle > 0 = LEFT convention, which
        // is the negative of the aerospace roll). Without this, a coordinated
        // corner drives a phantom pitch into the ±20° clamp and leaks
        // −g·sin(20°) ≈ −3.36 m/s² of braking into the longitudinal channel.
        const decoupledPitchRate =
          pitchRate * Math.cos(rollRadForPitch) +
          yawRate * Math.sin(rollRadForPitch)
        currentPitch += decoupledPitchRate * dt
        currentYaw += yawRate * dt
      }

      gyroIndex++
    }

    // Fuse with accelerometer for pitch (prevents drift). Requires a GPS
    // speed trend to de-dynamicize the x-axis; at stream edges (trend null)
    // hold the gyro-integrated pitch instead of fusing a contaminated value.
    const gpsTrend = gpsSpeedTrendMs2(gpsPoints, i)
    if (accelReading && gpsTrend !== null) {
      const factor = accelReading.unit === 'g' ? 9.81 : 1
      const accelPitch = getPitchFromAccelerometer(
        accelReading.x * factor,
        accelReading.y * factor,
        accelReading.z * factor,
        gpsTrend
      )

      // Complementary filter for pitch
      currentPitch = GYRO_WEIGHT * currentPitch + ACCEL_WEIGHT * accelPitch
    }

    // Clamp pitch to reasonable motorcycle range (-20° to +20°)
    currentPitch = Math.max(-20, Math.min(20, currentPitch))

    // Roll comes from pre-calculated lean angle
    currentRoll = preCalcRoll

    orientations.push({
      rollDeg: currentRoll,
      pitchDeg: currentPitch,
      yawDeg: currentYaw,
    })
  }

  return orientations
}

/**
 * Transform accelerometer readings from bike frame to earth frame,
 * accounting for both lean (roll) and pitch angles.
 *
 * Bike frame:
 *   X = forward (longitudinal)
 *   Y = right (lateral)
 *   Z = down (vertical)
 *
 * Earth frame (what we want):
 *   X = forward along road
 *   Y = right along road (horizontal)
 *   Z = down (gravity direction)
 *
 * Rotation order: First roll (lean), then pitch
 * - Roll (θ): Rotation around X-axis (lean left/right)
 * - Pitch (φ): Rotation around Y-axis (nose up/down)
 */
function transformToEarthFrame3D(
  accelX: number, // longitudinal (bike frame)
  accelY: number, // lateral (bike frame)
  accelZ: number, // vertical (bike frame)
  rollDeg: number, // lean angle
  pitchDeg: number // pitch angle
): { earthX: number; earthY: number; earthZ: number } {
  const rollRad = (rollDeg * Math.PI) / 180
  const pitchRad = (pitchDeg * Math.PI) / 180

  const cosRoll = Math.cos(rollRad)
  const sinRoll = Math.sin(rollRad)
  const cosPitch = Math.cos(pitchRad)
  const sinPitch = Math.sin(pitchRad)

  // Combined rotation matrix R = Ry(pitch) * Rx(roll)
  // This transforms from bike frame to earth frame

  // Step 1: Apply roll rotation (around X-axis)
  const y1 = accelY * cosRoll - accelZ * sinRoll
  const z1 = accelY * sinRoll + accelZ * cosRoll
  const x1 = accelX

  // Step 2: Apply pitch rotation (around Y-axis)
  const earthX = x1 * cosPitch + z1 * sinPitch
  const earthY = y1
  const earthZ = -x1 * sinPitch + z1 * cosPitch

  return { earthX, earthY, earthZ }
}

/**
 * Remove gravity from earth-frame acceleration to get dynamic acceleration only
 *
 * In earth frame, gravity is always [0, 0, -9.81] (pointing down)
 * Actual acceleration = measured - gravity
 */
function removeGravity(
  earthX: number,
  earthY: number,
  earthZ: number
): { dynamicX: number; dynamicY: number; dynamicZ: number } {
  const GRAVITY = 9.81

  return {
    dynamicX: earthX, // No gravity component in forward direction
    dynamicY: earthY, // No gravity component in lateral direction (horizontal)
    dynamicZ: earthZ + GRAVITY, // Remove downward gravity (Z is down, gravity is -9.81)
  }
}

function calculateGpsLongitudinal(
  current: GpsPoint,
  previous: GpsPoint | undefined
): number {
  if (!previous) return 0

  const deltaSpeed = (current.speedKmh - previous.speedKmh) / 3.6 // to m/s
  const deltaTime =
    (current.timestamp.getTime() - previous.timestamp.getTime()) / 1000

  if (deltaTime <= 0 || deltaTime > 1) return 0 // Sanity check

  return deltaSpeed / deltaTime
}

/**
 * Calculate acceleration using sensor fusion.
 *
 * Combines accelerometer + gyroscope data with lean angle to produce
 * gravity-corrected acceleration values in the earth/road frame.
 *
 * When gyroscope is available:
 * - Tracks full 3D orientation (roll + pitch + yaw)
 * - Pitch correction is critical for accurate braking/acceleration values
 * - During hard braking, bike pitches forward ~5-10°, contaminating readings
 *
 * @param gpsPoints - Processed GPS points
 * @param accelerometer - Raw accelerometer readings
 * @param leanAngle - Pre-calculated lean angle (from sensor fusion or other method)
 * @param frequencyConfig - Frequency configuration for smoothing
 * @param gyroscope - Optional raw gyroscope readings for 3D orientation
 * @param config - Fusion configuration
 */
export function calculateAccelerationWithSensorFusion(
  gpsPoints: GpsPoint[],
  accelerometer: AccelerometerReading[],
  leanAngle: LeanAnglePoint[],
  frequencyConfig: FrequencyAdaptiveConfig,
  gyroscope: GyroscopeReading[] = [],
  config: Partial<AccelerationFusionConfig> = {}
): AccelerationPoint[] {
  const cfg = { ...DEFAULT_CONFIG, ...config }

  if (gpsPoints.length === 0) return []

  // Normalize sensor data into the assumed bike frame (x fwd, y right,
  // z down, at-rest accel (0,0,−1)g). Devices differ in axis convention
  // (RaceBox is z-up) and mounts are arbitrary (the field unit that exposed
  // this was tilted ~33° nose-down); every derivation below assumes the
  // normalized frame. An untrusted calibration is the identity — data
  // passes through exactly as before.
  const calibration = estimateSensorFrameCalibration(accelerometer, gpsPoints)
  const calibratedAccelerometer = applySensorFrameCalibration(
    accelerometer,
    calibration
  )
  const calibratedGyroscope = applySensorFrameCalibration(
    gyroscope,
    calibration
  )

  // Calculate 3D orientation if gyroscope is available
  const has3DOrientation = calibratedGyroscope.length > 0
  // The decoupling term keeps its DIRECT GPS coordinated-lean feed even
  // though the fused lean is now corrected (its calculator uses the same
  // gpsCoordinatedLean physics as its reference). Deliberate: measured on
  // session-143033, feeding the fused lean instead reads +1.12 m/s² of
  // phantom accel through the t≈73 s GPS-dropout recovery vs +0.28 with the
  // direct feed (exaggerated points 36 vs 31) — the fused lean's
  // turn-radius reference smears position garbage over its wider smoothing
  // window and has no pre-blend clamp, and the complementary filter adds
  // lag, while the direct series is tightly windowed (±3 samples), hard
  // clamped (±65°) and falls back per-point on degenerate headings. Both
  // paths share one definition of the physics and of "a leaning vehicle"
  // (gpsCoordinatedLean.ts).
  const orientations = has3DOrientation
    ? track3DOrientation(
        calibratedAccelerometer,
        calibratedGyroscope,
        leanAngle,
        gpsPoints,
        selectDecouplingLeanSeries(
          gpsPoints,
          calibratedAccelerometer,
          computeGpsCoordinatedLeanSeries(gpsPoints)
        )
      )
    : null

  let accelIndex = 0
  const results: AccelerationPoint[] = []
  const firstWindowStartMs = firstWindowStartExclusiveMs(gpsPoints)

  for (let i = 0; i < gpsPoints.length; i++) {
    const point = gpsPoints[i]

    // Get orientation - use 3D if available, otherwise just lean (roll)
    const roll =
      orientations?.[i]?.rollDeg ?? leanAngle[i]?.leanAngleDegrees ?? 0
    const pitch = orientations?.[i]?.pitchDeg ?? 0

    // Average accelerometer samples in this GPS tick's window (anti-alias
    // ride vibration - see averageReadingsInWindow) instead of picking the
    // single nearest sample.
    const accelResult = averageReadingsInWindow(
      calibratedAccelerometer,
      i > 0 ? gpsPoints[i - 1].timestamp.getTime() : firstWindowStartMs,
      point.timestamp.getTime(),
      accelIndex
    )
    accelIndex = accelResult.index
    const accelReading = accelResult.reading

    let longitudinal = 0
    let lateral = 0
    let vertical = 0

    if (accelReading) {
      // Convert to m/s² if needed
      const factor = accelReading.unit === 'g' ? 9.81 : 1
      const rawX = accelReading.x * factor
      const rawY = accelReading.y * factor
      const rawZ = accelReading.z * factor

      // Step 1: Transform to earth frame (accounting for roll AND pitch)
      const earth = transformToEarthFrame3D(rawX, rawY, rawZ, roll, pitch)

      // Step 2: Remove gravity to get dynamic acceleration
      const dynamic = removeGravity(earth.earthX, earth.earthY, earth.earthZ)

      longitudinal = dynamic.dynamicX
      lateral = dynamic.dynamicY
      vertical = dynamic.dynamicZ

      // Step 3: GPS validation for longitudinal
      const gpsLongitudinal = calculateGpsLongitudinal(
        point,
        i > 0 ? gpsPoints[i - 1] : undefined
      )
      const gpsTrend = gpsSpeedTrendMs2(gpsPoints, i)

      const signMismatch =
        gpsTrend !== null &&
        Math.abs(gpsTrend) >= ACCEL_CONSISTENCY_MIN_TREND_MS2 &&
        Math.abs(longitudinal) >= ACCEL_CONSISTENCY_MIN_SIGNAL_MS2 &&
        Math.sign(longitudinal) !== Math.sign(gpsTrend)

      const attenuatedSignal =
        gpsTrend !== null &&
        Math.abs(gpsTrend) >= ACCEL_CONSISTENCY_RELATIVE_TREND_MS2 &&
        Math.abs(longitudinal) <
          ACCEL_CONSISTENCY_RELATIVE_SIGNAL_FRACTION * Math.abs(gpsTrend)

      if (signMismatch || attenuatedSignal) {
        // The GPS speed trend cannot be wrong about accelerating vs braking
        // over a ~1.4 s window; a contradicting or implausibly attenuated
        // fused value is. Weight the trend heavily.
        longitudinal =
          cfg.signMismatchFusedWeight * longitudinal +
          (1 - cfg.signMismatchFusedWeight) * gpsTrend
      } else {
        const longDeviation = Math.abs(longitudinal - gpsLongitudinal)
        if (longDeviation > cfg.gpsCorrectThreshold) {
          // Blend with GPS estimate
          longitudinal =
            (1 - cfg.gpsValidationWeight) * longitudinal +
            cfg.gpsValidationWeight * gpsLongitudinal
        }
      }
    } else {
      // No accelerometer - fall back to GPS-derived (basic)
      longitudinal = calculateGpsLongitudinal(
        point,
        i > 0 ? gpsPoints[i - 1] : undefined
      )
      // Lateral would need turn radius calculation (handled elsewhere)
    }

    results.push({
      longitudinalMs2: Math.round(longitudinal * 100) / 100,
      lateralMs2: Math.round(lateral * 100) / 100,
      verticalMs2: Math.round(vertical * 100) / 100,
      timestamp: point.timestamp,
      distanceMeters: point.distanceMeters,
      elapsedMs: point.elapsedMs,
      source: has3DOrientation
        ? ('sensor-fusion' as const)
        : ('accelerometer' as const),
    })
  }

  // Apply smoothing if configured
  if (cfg.smoothOutput && results.length > 10) {
    const smoothingWindow = Math.round(5 * frequencyConfig.frequencyScale)

    const longValues = results.map((p) => p.longitudinalMs2)
    const latValues = results.map((p) => p.lateralMs2)

    const smoothedLong = smoothValuesGaussian(
      longValues,
      smoothingWindow,
      frequencyConfig.smoothingPasses
    )
    const smoothedLat = smoothValuesGaussian(
      latValues,
      smoothingWindow,
      frequencyConfig.smoothingPasses
    )

    results.forEach((point, idx) => {
      point.longitudinalMs2 = Math.round(smoothedLong[idx] * 100) / 100
      point.lateralMs2 = Math.round(smoothedLat[idx] * 100) / 100
    })
  }

  return results
}
